import React, { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

/**
 * 勾選框（design system primitive，Figma v2 `checkbox_icons` 2128:20700 / 75:9273）。
 *
 * 為什麼不直接用 Bootstrap 的 `.form-check-input`:
 *  1. 尺寸對不上 —— Bootstrap 是 `1em`,本專案 $font-size-base 是 12px,
 *     設計稿要的是固定 16px。
 *  2. 模板的 `custom/components/_forms.scss` 有一組 `.form-checkbox-outline` /
 *     `.form-radio-outline`,裡面用 `background-color: … !important` 把勾選後的
 *     底色壓成白、再用 `background-image: none` 把勾勾拿掉。只要哪天有人把那個
 *     class 加到外層(或再多一條類似的覆寫),勾選狀態就整個消失 —— 這正是
 *     「點了不會變藍」的那類災情。自己擁有樣式就不會踩到。
 *
 * 有文案時**不要**把 input 包進 `<label>`:點框本身時,瀏覽器會先切一次
 * checkbox,label 的預設行為再對同一顆送一次 click,受控狀態就被打回未勾。
 * 登入頁「記住我」看起來只有聚焦環、沒有勾勾,就是這個(截圖像素是
 * `:focus-visible:not(:checked)` 的 `#a4baed` 框 + 白底)。文案用
 * `<label htmlFor>` 綁,點字、點框都只切一次。
 *
 * 規格:16×16、圓角 2;未勾為白底 + 1px `#abb5be` 框;
 * 已勾為 `#4975da` 實底 + 白色勾勾;indeterminate 為同底色 + 白色橫線。
 *
 * 仍然是真的 `<input type="checkbox">`,鍵盤與讀螢幕的行為都保留;
 * 外觀走 `appearance: none` + 自己的 CSS(見 custom/components/_dck-figma.scss)。
 */
export const Checkbox = ({
  id,
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  label,
  className,
  ...rest
}) => {
  const ref = useRef(null);
  const generatedId = useId();
  const inputId = id || generatedId;

  // indeterminate 只有 DOM property,沒有對應的 HTML attribute
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);

  const input = (
    <input
      ref={ref}
      id={inputId}
      type="checkbox"
      className={classnames("dck-checkbox", className)}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.checked, e)}
      {...rest}
    />
  );

  // 沒有 label 就只回勾選框本身(例如放在卡片標題列、由旁邊的文字負責語意)
  if (!label) return input;

  return (
    <span className="dck-checkbox-label">
      {input}
      <label htmlFor={inputId}>{label}</label>
    </span>
  );
};
Checkbox.propTypes = {
  id: PropTypes.string,
  checked: PropTypes.bool,
  /** 半選(部分子項被選):顯示橫線而不是勾勾 */
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
  /** (checked: boolean, event) => void */
  onChange: PropTypes.func,
  label: PropTypes.node,
  className: PropTypes.string,
};

export default Checkbox;
